import axios from "axios";
import CommonDropzone from "components/common/CommonDropzone";
import CommonTextField from "components/common/CommonTextField";
import FilledButton from "components/InterstellrComponents/FilledButton";
import React, { useEffect, useState } from "react";
import { IoCloseOutline } from "react-icons/io5";
import { generateURLOptions } from "URLOptions";
import {
  CREATE_RECIPES,
  GET_RECIPE_DETAILS,
  GET_RECIPES,
  UPDATE_RECIPE,
  UPLOAD_VIRTUAL_COMMUTE_IMAGE,
} from "URLRoutes";

const EditRecipe = ({ isOpen, onClose, item, onComplete }) => {
  const [categories, setCategories] = useState([]);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState(null);
  const [imageURL, setImageURL] = useState("");
  const [kj, setKj] = useState("");
  const [servings, setServings] = useState("");

  const [ingredients, setIngredients] = useState("");
  const [method, setMethod] = useState("");
  const [nutritionalValue, setNutritionalValue] = useState("");
  const [healthFact, setHealthFact] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setTitle("");
      setCategory(null);
      setKj("");
      setServings("");
      setIngredients("");
      setMethod("");
      setNutritionalValue("");
      setHealthFact("");
      setDescription("");
      setImageURL("");
    } else if (item?._id) {
      getRecipeCategories();
      getRecipe();
    }
  }, [isOpen, item]);

  const getRecipeCategories = async () => {
    try {
      const response = await axios.get(GET_RECIPES, {
        headers: generateURLOptions(),
      });
      if (response?.data?.success === false) {
        alert(response?.data?.message ?? "");
        return;
      }

      setCategories(response?.data?.data);
    } catch (error) {
      console.error("Get Recipes Error: ", error);
      alert(error);
    }
  };

  const uploadImage = async (image) => {
    const formData = new FormData();
    formData.append("image", image);
    try {
      const response = await axios.post(
        UPLOAD_VIRTUAL_COMMUTE_IMAGE,
        formData,
        {
          headers: generateURLOptions("multipart/form-data"),
        }
      );
      if (response?.data?.success === false) {
        return alert(response?.data?.message ?? "");
      }
      setImageURL(response.data?.data);
    } catch (error) {
      console.error("Upload Recipe Image Error: ", error);
      alert(error);
    }
  };

  const getRecipe = async () => {
    try {
      const response = await axios.get(GET_RECIPE_DETAILS + item?._id, {
        headers: generateURLOptions(),
      });

      if (response?.data?.success === false) {
        alert(response?.data?.message ?? "");
        return;
      }

      const data = response?.data?.data;
      if (!data) return;

      const parseArrayToText = (value) => {
        if (Array.isArray(value)) {
          return value
            .map((v) => {
              let text = "";
              if (typeof v === "string") text = v;
              else if (typeof v === "object" && v !== null) {
                text =
                  v.ingredient ||
                  v.description ||
                  Object.values(v)[0] ||
                  JSON.stringify(v);
              } else {
                text = String(v);
              }

              // Ensure each item starts with "- "
              text = text.trim().startsWith("-")
                ? text.trim()
                : `- ${text.trim()}`;
              return text;
            })
            .join("\n");
        }

        if (typeof value === "object" && value !== null) {
          return Object.values(value)
            .map((v) => {
              const text = String(v).trim();
              return text.startsWith("-") ? text : `- ${text}`;
            })
            .join("\n");
        }

        const text = (value || "").trim();
        return text ? (text.startsWith("-") ? text : `- ${text}`) : "";
      };

      setTitle(data.title || "");
      setCategory(data.parent_id || null);
      setKj(data.kilojoules || "");
      setServings(data.servings || "");
      setIngredients(parseArrayToText(data.ingredients));
      setMethod(parseArrayToText(data.description));
      setNutritionalValue(data.nutritional_value || "");
      setHealthFact(data.health_fact || "");
      setImageURL(data.image || "");
    } catch (error) {
      console.error("Get Recipe Error: ", error);
      alert(error);
    }
  };

  const updateRecipe = async (data) => {
    try {
      const response = await axios.post(UPDATE_RECIPE, data, {
        headers: generateURLOptions(),
      });
      if (response?.data?.success === false) {
        alert(response?.data?.message ?? "");
        return;
      }

      onComplete();
    } catch (error) {
      console.error("Get Recipes Error: ", error);
      alert(error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl w-2/3 h-4/5 p-6 relative flex flex-col overflow-scroll overflow-x-hidden">
        <div className="grid grid-cols-2 gap-6">
          <div>
            {/* Header */}
            <div className="flex justify-center items-center mb-4">
              <h2 className="text-lg font-semibold">
                Update {item?.title ?? ""}
              </h2>
              <div style={{ flex: 1 }} />{" "}
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700"
              >
                <IoCloseOutline size={24} />
              </button>
            </div>

            {/* Body */}
            <div className="space-y-4">
              {/* Image Upload Area */}
              <div>
                <p className="mb-1 font-semibold text-sm text-gray-800">
                  Image
                </p>
                <div className="flex gap-3 items-start mb-3">
                  {imageURL && (
                    <img
                      src={imageURL}
                      alt="Mindfulness Upload"
                      className="w-2/5 h-auto object-cover rounded-lg"
                    />
                  )}
                  <CommonDropzone
                    title="Image"
                    value={imageURL}
                    dropFile={uploadImage}
                  />
                </div>
              </div>

              <CommonTextField
                label="Recipe Name"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Recipe Name"
              />

              <div>
                <p className="mb-1 font-semibold text-sm text-gray-800">
                  Exercise Category
                </p>
                <select
                  onChange={(e) => setCategory(e.target.value)}
                  value={category ?? ""}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="">Select a category</option>
                  {categories?.map((category) => (
                    <option key={category?._id} value={category?._id}>
                      {category?.name ?? ""}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div>
            {/* Recipe Details */}
            <div className="flex flex-row justify-around items-center">
              {/* Servings */}
              <div className="flex flex-col justify-center items-center">
                <h4 className="text-black font-semibold text-lg">Servings</h4>
                <div className="mt-2 w-16 h-16 bg-[#5153F5] rounded-full relative">
                  <input
                    className="absolute top-0 bottom-0 left-0 right-0 bg-transparent text-white font-medium text-2xl text-center"
                    onChange={(e) => setServings(e.target.value)}
                    value={servings}
                  />
                </div>
              </div>

              {/* Kilojoules */}
              <div className="flex flex-col justify-center items-center">
                <h4 className="text-black font-semibold text-lg">
                  Kilojoules (kJ)
                </h4>
                <div className="mt-2 w-16 h-16 bg-[#5153F5] rounded-full relative">
                  <input
                    className="absolute top-0 bottom-0 left-0 right-0 bg-transparent text-white font-medium text-2xl text-center"
                    onChange={(e) => setKj(e.target.value)}
                    value={kj}
                  />
                </div>
              </div>
            </div>

            {/* Ingredients Area */}
            <CommonTextField
              label="Ingredients"
              value={ingredients}
              multiline={true}
              onChange={(e) => {
                console.log("Ingredients: ", e.target.value);
                setIngredients(e.target.value);
              }}
              placeholder="Ingredients"
            />

            {/* Method */}
            <CommonTextField
              label="Method"
              value={method}
              multiline={true}
              onChange={(e) => {
                console.log("Method: ", e.target.value);
                setMethod(e.target.value);
              }}
              placeholder="Method"
            />

            {/* Nutritional Value */}
            <CommonTextField
              label="Nutritional Value"
              value={nutritionalValue}
              multiline={true}
              onChange={(e) => {
                console.log("Nutritional Value: ", e.target.value);
                setNutritionalValue(e.target.value);
              }}
              placeholder="Nutritional Value"
            />

            {/* Heatlh Fact */}
            <CommonTextField
              label="Health Fact"
              value={healthFact}
              multiline={true}
              onChange={(e) => {
                console.log("Health Fact: ", e.target.value);
                setHealthFact(e.target.value);
              }}
              placeholder="Health Fact"
            />
          </div>

          <div className="h-10 flex justify-between space-x-4">
            <FilledButton
              text={"Cancel"}
              marginTop={0}
              onPress={onClose}
              backgroundColor="#d33"
            />

            <FilledButton
              text={"Update Recipe"}
              marginTop={0}
              onPress={() =>
                updateRecipe({
                  itemId: item?._id,
                  title,
                  description,
                  image: imageURL,
                  kj,
                  servings,
                  ingredients,
                  method,
                  nutritionalValue,
                  healthFact,
                  category,
                })
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditRecipe;
